'use client'

import { useEffect } from 'react'
import { useSession } from 'next-auth/react'
import { useReviewUnreadCount } from '@/hooks/useReview'
import type { NavBadgeKey } from '@/lib/navigation'
import { REVIEWER_HOME, canAccess } from '@/lib/roles'
import { REVIEW_UPDATED_EVENT } from '@/lib/review-view'

/**
 * Live counts for nav entries that declare a `badge` (plan 04 A4: the
 * Approvals entry shows unseen approval requests). Only fetched when the
 * session may open the entry, so other roles never call `/api/review/*`.
 */
export function useNavBadges(): Partial<Record<NavBadgeKey, number>> {
  const { data: session, status } = useSession()
  const enabled = status === 'authenticated' && canAccess(REVIEWER_HOME, session?.roles)
  const unread = useReviewUnreadCount(enabled)
  const refresh = unread.refresh

  useEffect(() => {
    if (!enabled) return
    const onUpdate = () => void refresh()
    window.addEventListener(REVIEW_UPDATED_EVENT, onUpdate)
    return () => window.removeEventListener(REVIEW_UPDATED_EVENT, onUpdate)
  }, [enabled, refresh])

  return { 'review-unread': enabled ? unread.data ?? 0 : 0 }
}
